import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Context = {
  isLoading?: boolean;
  setComponentLoaded?: (componentName: string) => void;
};

type Props = {
  children: ReactNode;
  components: string[];
};

const PageLoadingContext = createContext<Context>({});

const PageLoadingProvider = ({ children, components }: Props) => {
  const stringifiedComponents = JSON.stringify(components);

  const [loadingComponents, setLoadingComponents] = useState(
    components.reduce((state, componentName) => ({ ...state, [componentName]: true }), {})
  );

  useEffect(() => {
    setLoadingComponents((prevState: Record<string, boolean>) =>
      JSON.parse(stringifiedComponents).reduce(
        (state: Record<string, boolean>, componentName: string) => ({
          ...state,
          ...(prevState[componentName] !== undefined && { [componentName]: prevState[componentName] }),
        }),
        {}
      )
    );
  }, [stringifiedComponents]);

  const isLoading = Object.values(loadingComponents).some(value => value);

  const setComponentLoaded = useCallback(
    (componentName: string) => {
      setLoadingComponents(previousIsLoading => ({ ...previousIsLoading, [componentName]: false }));
    },
    [setLoadingComponents]
  );

  const value = useMemo(() => ({ isLoading, setComponentLoaded }), [isLoading, setComponentLoaded]);

  return <PageLoadingContext.Provider value={value}>{children}</PageLoadingContext.Provider>;
};

const usePageLoading = () => useContext(PageLoadingContext);

export { PageLoadingProvider, usePageLoading };
